import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Megaphone, ExternalLink } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

interface Announcement {
  id: string;
  title: string;
  description: string;
  target_date: string;
  link_url: string;
  updated_at?: string;
}

const API_URL = (import.meta as any).env?.VITE_API_URL || 'http://localhost:3001';

export function AnnouncementPopup() {
  const [announcement, setAnnouncement] = useState<Announcement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    // Only fetch if authenticated (because our API requires auth right now)
    // If the API was public, we could fetch without session.
    if (!isAuthenticated) return;

    const fetchActiveAnnouncement = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) return;

        const response = await fetch(`${API_URL}/api/announcement/active?t=${Date.now()}`, {
          headers: {
            'Authorization': `Bearer ${session.access_token}`
          },
          cache: 'no-store'
        });
        const data = await response.json();
        
        if (response.ok && data.ok && data.data) {
          const ann = data.data;
          // The backend now filters out announcements that have reached max_shows for this user
          // We can simply show it directly
          setAnnouncement(ann);
          setIsOpen(true);
        }
      } catch (error) {
        console.error('Failed to fetch announcement:', error);
      }
    };

    fetchActiveAnnouncement();
  }, [isAuthenticated]);

  const [isProcessing, setIsProcessing] = useState(false);

  const handleDismiss = () => {
    if (!announcement || isProcessing) return;
    setIsProcessing(true);
    
    const currentAnnId = announcement.id;
    
    // Optimistic close: close UI immediately so user doesn't feel a freeze
    setAnnouncement(null);
    setIsOpen(false);
    
    // Background request to record view count
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.access_token) {
          await fetch(`${API_URL}/api/announcement/${currentAnnId}/read`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${session.access_token}`
            }
          });
        }
      } catch (error) {
        console.error('Failed to mark announcement as read:', error);
      }
    })();
  };

  if (!announcement) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open: boolean) => {
      if (!open) handleDismiss();
    }}>
      <DialogContent className="w-[92vw] sm:max-w-[500px] max-h-[85dvh] overflow-y-auto rounded-2xl p-5 sm:p-6">
        <DialogHeader className="flex flex-col items-center space-y-4 pt-2 sm:pt-4">
          <div className="w-16 h-16 bg-blue-500/10 rounded-full flex items-center justify-center mb-2">
            <Megaphone className="w-8 h-8 text-blue-500" />
          </div>
          <DialogTitle className="text-2xl font-bold text-center w-full">
            {announcement.title}
          </DialogTitle>
          {announcement.target_date && (
            <div className="text-sm font-semibold text-muted-foreground bg-muted px-3 py-1 rounded-full border">
              {new Date(announcement.target_date).toLocaleDateString('id-ID', { year: 'numeric', month: 'long', day: 'numeric' })}
            </div>
          )}
        </DialogHeader>
        
        <div className="py-4">
          <DialogDescription className="text-base text-left leading-relaxed whitespace-pre-wrap">
            {announcement.description}
          </DialogDescription>
        </div>

        <DialogFooter className="flex flex-col sm:flex-col gap-3 mt-4">
          {announcement.link_url && (
            <Button 
              className="w-full text-md h-12" 
              onClick={() => {
                window.open(announcement.link_url, '_blank');
                handleDismiss();
              }}
            >
              Cek Sekarang
              <ExternalLink className="w-4 h-4 ml-2" />
            </Button>
          )}
          <Button 
            variant={announcement.link_url ? "outline" : "default"} 
            className="w-full text-md h-12" 
            onClick={handleDismiss}
          >
            OK, Mengerti
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
